import { useEffect, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { useTrip, useUI } from "@/lib/store";
import { TRIP_DATES, fmtShort, fmtTime, parseTimeInput } from "@/lib/dates";
import { download, wrap, eventToV } from "@/lib/ics";
import { BottomSheet, Field, inputCls } from "./BottomSheet";

const CATS = [["activity", "Activity"], ["meal", "Meal"], ["note", "Note"], ["reminder", "Reminder"]];
const STATUSES = [["planned", "Planned"], ["confirmed", "Confirmed"], ["tbc", "TBC"], ["action_required", "Action required"], ["idea", "Idea"]];

export function EventEditor() {
  const { editor, closeEditor } = useUI();
  const { trip, addEvent, updateEvent, organiser, profile } = useTrip();
  const [f, setF] = useState(null);
  const [addCal, setAddCal] = useState(false);

  useEffect(() => {
    if (!editor) return setF(null);
    const e = editor.event || {};
    const idea = editor.idea;
    const date = e.date || editor.date || TRIP_DATES[0];
    setF({
      title: e.title || idea?.title?.replace(/ at night$/i, "") || "",
      date,
      startTime: e.startTime ? fmtTime(e.startTime) : "",
      endTime: e.endTime ? fmtTime(e.endTime) : "",
      city: e.city || trip.days.find((d) => d.date === date)?.city || "",
      category: e.category || (idea?.category === "note" ? "note" : idea?.category) || "activity",
      status: e.status || "planned",
      address: e.address || idea?.address || "",
      description: e.description || (idea?.notes || ""),
      notes: e.notes || (idea?.link ? `Link: ${idea.link}` : ""),
      private: e.private || false,
      visibility: e.visibility || "everyone",
    });
    setAddCal(false);
  }, [editor, trip.days]);

  if (!editor || !f) return null;
  const isEdit = editor.mode === "edit";
  const focusTime = editor.focus === "time";
  const setDate = (d) => setF({ ...f, date: d, city: trip.days.find((x) => x.date === d)?.city || f.city });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Give it a title");
    const st = f.startTime ? parseTimeInput(f.startTime) : null;
    const et = f.endTime ? parseTimeInput(f.endTime) : null;
    if (f.startTime && !st) return toast.error("Time not understood — try 7:30 PM");
    const payload = { title: f.title.trim(), date: f.date, startTime: st, endTime: et, category: f.category, status: f.status, description: f.description, notes: f.notes, address: f.address, private: f.private, city: f.city, visibility: f.visibility, ownerId: f.visibility === "mine" ? (editor.event?.ownerId || profile?.id || null) : (editor.event?.ownerId || profile?.id || null), ownerName: editor.event?.ownerName || profile?.name || null };
    let saved;
    if (isEdit) { await updateEvent(editor.event.id, payload); saved = { ...editor.event, ...payload }; toast.success("Updated"); }
    else { saved = await addEvent({ ...payload, source: editor.idea ? "idea" : "user", ideaId: editor.idea?.id || null }); toast.success(`Added to ${fmtShort(f.date)}`); }
    if (addCal) download(`${saved.title.replace(/[^a-z0-9]+/gi, "-")}.ics`, wrap([eventToV(saved)]));
    closeEditor();
  };

  return (
    <BottomSheet open onClose={closeEditor} testId="event-editor" eyebrow={isEdit ? "Edit item" : editor.idea ? "Add idea to itinerary" : "Add to itinerary"} title={isEdit ? f.title : editor.idea ? editor.idea.title : "New event"}>
      <div className="mt-4 space-y-3">
        <Field label="Title"><input data-testid="editor-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} /></Field>
        <div>
          <span className="eyebrow">Day</span>
          <div className="no-scrollbar mt-1 flex gap-2 overflow-x-auto pb-1">
            {TRIP_DATES.map((d) => (
              <button key={d} data-testid={`editor-date-${d}`} onClick={() => setDate(d)} className={`tap shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold ${f.date === d ? "border-[var(--jade)] bg-[var(--jade)] text-[var(--parchment)]" : "bg-white"}`}>
                {fmtShort(d)}<span className="block text-[9px] font-medium opacity-70">{trip.days.find((x) => x.date === d)?.city}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start (optional)"><input data-testid="editor-start-input" autoFocus={focusTime} placeholder="e.g. 7:30 PM" value={f.startTime} onChange={(e) => setF({ ...f, startTime: e.target.value })} className={`${inputCls} ${focusTime ? "border-[var(--coral)] ring-2 ring-[var(--coral)]/30" : ""}`} /></Field>
          <Field label="End (optional)"><input data-testid="editor-end-input" placeholder="e.g. 9 PM" value={f.endTime} onChange={(e) => setF({ ...f, endTime: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City"><select data-testid="editor-city-select" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inputCls + " text-sm"}>{trip.destinations.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select></Field>
          <Field label="Type"><select data-testid="editor-category-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls + " text-sm"}>{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        </div>
        <Field label="Location / address (optional)"><input data-testid="editor-address-input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} /></Field>
        <Field label="Notes (optional)"><textarea data-testid="editor-notes-input" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls + " text-sm"} /></Field>
        <Field label="Status"><select data-testid="editor-status-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls + " text-sm"}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <div><span className="eyebrow">Visible to</span>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {[["everyone", "Everyone"], ["mine", `My item${profile ? ` (${profile.name})` : ""}`]].map(([v, l]) => <button key={v} data-testid={`editor-visibility-${v}`} onClick={() => setF({ ...f, visibility: v })} className={`tap min-h-[44px] rounded-xl border px-3 text-sm font-semibold ${f.visibility === v ? "border-[var(--jade)] bg-[var(--jade)] text-[var(--parchment)]" : "bg-white"}`}>{l}</button>)}
          </div></div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {organiser && <label className="inline-flex items-center gap-2"><input data-testid="editor-private-check" type="checkbox" checked={f.private} onChange={(e) => setF({ ...f, private: e.target.checked })} /> Private (organiser only)</label>}
          <label className="inline-flex items-center gap-2"><input data-testid="editor-calendar-check" type="checkbox" checked={addCal} onChange={(e) => setAddCal(e.target.checked)} /> <CalendarPlus className="h-4 w-4" /> Also export to my personal calendar</label>
        </div>
      </div>
      <button data-testid="editor-save-btn" onClick={save} className="tap mt-5 w-full rounded-full bg-[var(--jade)] py-3.5 text-base font-semibold text-[var(--parchment)]">{isEdit ? "Save changes" : "Add event"}</button>
    </BottomSheet>
  );
}
